import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/wishlist";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import AddressBook from "@/components/AddressBook";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Scissors, Package, Heart, Award, MapPin, User, ChevronRight, Ruler } from "lucide-react";

export default function UserDashboard() {
  const { user, updateProfile } = useAuth();
  const { count: wishCount } = useWishlist();
  const nav = useNavigate();
  const [orders, setOrders] = useState({ orders: [], bespoke: [] });
  const [meas, setMeas] = useState([]);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [addr, setAddr] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });
  const [savingP, setSavingP] = useState(false);
  const [savingA, setSavingA] = useState(false);

  const refresh = async () => {
    if (!user) return;
    const [o, m] = await Promise.all([api.get("/orders"), api.get("/me/measurements")]);
    setOrders(o.data); setMeas(m.data);
  };
  useEffect(() => { if (user) { refresh(); setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "" }); const a = (user.addresses || [])[0]; if (a) setAddr({ label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "", ...a }); } }, [user]); // eslint-disable-line

  if (!user) return <div className="sq-container py-20 text-center"><Link to="/auth" className="sq-btn-primary">Sign in</Link></div>;

  const all = [...(orders.bespoke || []).map((o) => ({ ...o, _b: true })), ...(orders.orders || []).map((o) => ({ ...o, _b: false }))].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const active = all.filter((o) => !["delivered", "cancelled"].includes((o.stage || o.status || "").toLowerCase())).length;

  const saveProfile = async () => {
    setSavingP(true);
    try { await updateProfile({ name: profile.name, email: profile.email, phone: profile.phone.replace(/\D/g, "") }); toast.success("Profile updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setSavingP(false);
  };
  const saveAddress = async () => {
    setSavingA(true);
    try {
      const others = (user.addresses || []).slice(1);
      await updateProfile({ addresses: [addr, ...others] });
      toast.success("Address saved");
    } catch (e) { toast.error("Could not save address"); }
    setSavingA(false);
  };

  const stat = [
    { label: "Orders", value: all.length, icon: Package },
    { label: "In Progress", value: active, icon: Scissors },
    { label: "Wishlist", value: wishCount, icon: Heart, to: "/wishlist" },
    { label: "Loyalty", value: `${user.loyalty_points || 0} pts`, icon: Award },
  ];

  return (
    <main className="bg-[#FAFAF8] min-h-screen" data-testid="user-dashboard">
      {/* Hero header */}
      <div className="bg-gradient-to-br from-squire-jeans to-black text-white">
        <div className="sq-container py-10 md:py-14">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/15 grid place-items-center font-display text-2xl">{(user.name || "U").slice(0, 1).toUpperCase()}</div>
            <div>
              <div className="sq-eyebrow text-white/60">Your Atelier</div>
              <h1 className="font-display text-3xl md:text-4xl leading-none mt-1">Hello, {user.name || "there"}.</h1>
              <div className="text-white/70 text-sm mt-1">{user.phone ? `+91 ${user.phone}` : user.email}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/bespoke" className="bg-white text-black px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-white/90">Start a Bespoke</Link>
            <Link to="/track-order" className="border border-white/40 hover:border-white px-5 py-2.5 text-xs uppercase tracking-[0.15em]">Track an Order</Link>
          </div>
        </div>
      </div>

      <div className="sq-container py-8">
        {/* Snapshot */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stat.map((s) => {
            const Card = s.to ? Link : "div";
            return (
              <Card key={s.label} {...(s.to ? { to: s.to } : {})} className="bg-white border border-black/10 rounded-xl p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-squire-jeans/10 text-squire-jeans grid place-items-center"><s.icon size={18} /></div>
                <div><div className="font-display text-2xl leading-none">{s.value}</div><div className="sq-eyebrow text-[#9CA3AF] mt-1">{s.label}</div></div>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="bg-transparent p-0 h-auto gap-2 flex-wrap justify-start">
            {[["orders", "Orders"], ["bespoke", "Bespoke"], ["measurements", "Measurements"], ["profile", "Profile & Settings"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`dash-tab-${v}`} className="rounded-full border border-black/15 data-[state=active]:bg-black data-[state=active]:text-white px-4 py-2 text-xs uppercase tracking-[0.12em]">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="orders" className="mt-6 space-y-3">
            {all.length === 0 && <div className="bg-white border border-black/10 rounded-xl p-10 text-center text-[#4B5563]">No orders yet. <Link to="/bespoke" className="text-squire-jeans underline">Start one</Link>.</div>}
            {all.map((o) => (
              <Link key={o.id} to={`/track-order?id=${o.id}`} className="flex items-center gap-4 bg-white border border-black/10 rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-md transition-all">
                <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${o._b ? "bg-squire-jeans/10 text-squire-jeans" : "bg-black/5"}`}>{o._b ? <Scissors size={18} /> : <Package size={18} />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2"><span className="font-display text-lg truncate">{o.id}{o._b ? ` · ${o.garment_type || ""}` : ""}</span><span className="font-semibold">{formatINR(o.total)}</span></div>
                  <div className="flex items-center gap-2 mt-1.5"><StatusBadge status={o.stage || o.status || "placed"} />{o.balance_due > 0 && <span className="text-[11px] text-amber-700">balance {formatINR(o.balance_due)}</span>}</div>
                </div>
                <ChevronRight size={18} className="text-[#9CA3AF]" />
              </Link>
            ))}
          </TabsContent>

          <TabsContent value="bespoke" className="mt-6 space-y-3">
            {(orders.bespoke || []).length === 0 && <div className="bg-white border border-black/10 rounded-xl p-10 text-center text-[#4B5563]">No bespoke orders yet.</div>}
            {(orders.bespoke || []).map((b) => (
              <Link key={b.id} to={`/track-order?id=${b.id}`} className="flex items-center gap-4 bg-white border border-squire-jeans/30 rounded-xl p-4 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-full bg-squire-jeans/10 text-squire-jeans grid place-items-center"><Scissors size={18} /></div>
                <div className="flex-1"><div className="font-display text-lg">{b.id} · {b.garment_type}</div><div className="flex items-center gap-2 mt-1.5"><StatusBadge status={b.stage} /><span className="text-[11px] text-[#9CA3AF]">Advance {formatINR(b.advance_paid)} · Balance {formatINR(b.balance_due)}</span></div></div>
                <ChevronRight size={18} className="text-[#9CA3AF]" />
              </Link>
            ))}
          </TabsContent>

          <TabsContent value="measurements" className="mt-6">
            {meas.length === 0 ? <div className="bg-white border border-black/10 rounded-xl p-10 text-center text-[#4B5563]">No measurement profiles saved.</div> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {meas.map((m, i) => (
                  <div key={i} className="bg-white border border-black/10 rounded-xl p-5">
                    <div className="flex items-center gap-2 font-medium"><Ruler size={16} className="text-squire-jeans" /> {m.profile_name || "Profile"}</div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      {Object.entries(m).filter(([k, v]) => v && !["user_id", "id", "profile_name", "updated_at"].includes(k)).map(([k, v]) => (
                        <div key={k}><span className="text-[#9CA3AF] capitalize">{k.replace(/_/g, " ")}</span> {v}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-black/10 rounded-xl p-6">
                <div className="flex items-center gap-2 font-display text-xl"><User size={18} className="text-squire-jeans" /> Personal Details</div>
                <div className="space-y-4 mt-5">
                  {[["name", "Full Name"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
                    <label key={k} className="block"><span className="sq-eyebrow text-[10px]">{l}</span>
                      <input data-testid={`profile-${k}`} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-squire-jeans" />
                    </label>
                  ))}
                  <button data-testid="profile-save" onClick={saveProfile} disabled={savingP} className="sq-btn-primary w-full justify-center">{savingP ? "Saving…" : "Save Details"}</button>
                </div>
              </div>

              <div className="bg-white border border-black/10 rounded-xl p-6">
                <div className="flex items-center gap-2 font-display text-xl mb-5"><MapPin size={18} className="text-squire-jeans" /> Saved Addresses</div>
                <AddressBook manageOnly />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
